// Chakra imports
import {
  Box,
  Button,
  Flex,
  Icon,
  Image,
  Link,
  Spinner,
  Text,
  useColorModeValue,
} from "@chakra-ui/react";
// Custom components
import Card from "components/card/Card.js";
// Assets
import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { IoHeart, IoHeartOutline } from "react-icons/io5";
import { MdShowChart } from "react-icons/md";
import { useNavigate } from "react-router-dom";

export default function Network(props) {
  const { t } = useTranslation();
  const { image, name, author, download, currentbid, payload } = props;
  const [like, setLike] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const textColor = useColorModeValue("navy.700", "white");
  const textColorBid = useColorModeValue("brand.500", "white");
  
  // Analytics button colors
  const buttonBg = useColorModeValue("brand.500", "brand.400");
  const buttonHoverBg = useColorModeValue("brand.600", "brand.300");
  const buttonActiveBg = useColorModeValue("brand.700", "brand.500");
  const buttonBoxShadow = useColorModeValue("0 4px 12px rgba(66, 42, 251, 0.3)", "0 4px 12px rgba(117, 81, 255, 0.4)");
  
  // Like button colors
  const likeBg = useColorModeValue("white", "navy.700");
  const likeHoverBg = useColorModeValue("whiteAlpha.900", "navy.600");
  const likeActiveBg = useColorModeValue("white", "navy.700");
  const likeIconColor = useColorModeValue("brand.500", "brand.300");
  const likeBoxShadow = useColorModeValue("0 4px 12px rgba(0, 0, 0, 0.15)", "0 4px 12px rgba(255, 255, 255, 0.1)");
  
  // Card styling when liked
  const cardBorder = useColorModeValue(
    like ? "2px solid" : "1px solid transparent", 
    like ? "2px solid" : "1px solid transparent"
  );
  const cardBorderColor = useColorModeValue("red.200", "red.300");
  const cardHoverShadow = useColorModeValue("0 8px 25px rgba(0, 0, 0, 0.1)", "0 8px 25px rgba(0, 0, 0, 0.3)");

  useEffect(() => {
    // Simulate a loading delay
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    // Load favorite status from localStorage
    const savedLikeStatus = localStorage.getItem(`network-favorite-${payload?.id || name}`);
    if (savedLikeStatus !== null) {
      setLike(JSON.parse(savedLikeStatus));
    }

    return () => clearTimeout(timer);
  }, [payload?.id, name]);

  const handleAnalyticsClick = () => {
    const networkData = payload;
    navigate('/admin/analytics', { 
      state: { network: networkData } 
    });
  };

  const handleLikeClick = () => {
    const newLikeStatus = !like;
    setLike(newLikeStatus);
    localStorage.setItem(`network-favorite-${payload?.id || name}`, JSON.stringify(newLikeStatus));
  };

  return (
    <Card 
      p='20px' 
      border={cardBorder}
      borderColor={like ? cardBorderColor : "transparent"}
      transition="all 0.3s ease"
      _hover={{
        transform: "translateY(-2px)",
        boxShadow: cardHoverShadow
      }}
    >
      {loading ? (
        <Flex justify='center' align='center' h='100%'>
          <Spinner size='xl' />
        </Flex>
      ) : (
        <Flex direction={{ base: "column" }} justify='center'>
          <Box mb={{ base: "20px", "2xl": "20px" }} position='relative'>
            <Image
              src={image}
              w={{ base: "180px", md: "80%" }}
              maxW={{ base: "180px", md: "80%" }}
              h='auto'
              borderRadius='20px'
              mx='auto'
              display='block'
            />
            <Button
              position='absolute'
              bg={likeBg}
              _hover={{ 
                bg: likeHoverBg,
                transform: "scale(1.1)",
                boxShadow: likeBoxShadow
              }}
              _active={{ 
                bg: likeActiveBg,
                transform: "scale(0.95)"
              }}
              p='0px !important'
              top='14px'
              right='14px'
              borderRadius='50%'
              minW='36px'
              h='36px'
              transition="all 0.2s ease"
              onClick={handleLikeClick}>
              <Icon
                transition='0.2s linear'
                w='20px'
                h='20px'
                as={like ? IoHeart : IoHeartOutline}
                color={like ? "red.500" : likeIconColor}
              />
            </Button>
          </Box>
          <Flex flexDirection='column' justify='space-between' h='100%'>
            <Flex
              justify='space-between'
              direction={{
                base: "row",
                md: "column",
                lg: "row",
                xl: "column",
                "2xl": "row",
              }}
              mb='auto'>
              <Flex direction='column'>
                <Text
                  color={textColor}
                  fontSize={{
                    base: "xl",
                    md: "lg",
                    lg: "lg",
                    xl: "lg",
                    "2xl": "md",
                    "3xl": "lg",
                  }}
                  mb='5px'
                  fontWeight='bold'
                  me='14px'>
                  {name}
                </Text>
                <Text
                  color='secondaryGray.600'
                  fontSize={{
                    base: "sm",
                  }}
                  fontWeight='400'
                  me='14px'>
                  {author}
                </Text>
              </Flex>
            </Flex>
            <Flex
              align='start'
              justify='space-between'
              direction={{
                base: "row",
                md: "column",
                lg: "row",
                xl: "column",
                "2xl": "row",
              }}
              mt='25px'>
              <Text fontWeight='700' fontSize='sm' color={textColorBid}>
                {currentbid}
              </Text>
              <Flex
                gap={2}
                direction={{
                  base: "row",
                  md: "column",
                  lg: "row",
                  xl: "column",
                  "2xl": "row",
                }}
                mt={{
                  base: "0px",
                  md: "10px",
                  lg: "0px",
                  xl: "10px",
                  "2xl": "0px",
                }}>
                <Button
                  size="sm"
                  bg={buttonBg}
                  color="white"
                  _hover={{
                    bg: buttonHoverBg,
                    transform: "translateY(-2px)",
                    boxShadow: buttonBoxShadow
                  }}
                  _active={{
                    bg: buttonActiveBg,
                    transform: "translateY(0px)"
                  }}
                  leftIcon={<Icon as={MdShowChart} />}
                  onClick={handleAnalyticsClick}
                  fontWeight="600"
                  transition="all 0.2s"
                >
                  {t('Analytics')}
                </Button>
                <Link href={download}>
                </Link>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      )}
    </Card>
  );
}
